import type { AuditionFile, Project, SyncManifest, SyncManifestEntry } from "./types";
import { safeWindowsName, sha256 } from "./utils";

declare global {
  interface Window { showDirectoryPicker?: (options?: { mode?: "read" | "readwrite" }) => Promise<FileSystemDirectoryHandle>; }
}

const MANIFEST_NAME = "_shiyin_sync_manifest.json";
const emptyManifest = (): SyncManifest => ({ version: 1, updatedAt: new Date().toISOString(), files: {} });

async function readManifest(root: FileSystemDirectoryHandle): Promise<SyncManifest> {
  try {
    const handle = await root.getFileHandle(MANIFEST_NAME);
    const parsed = JSON.parse(await (await handle.getFile()).text());
    return parsed?.version === 1 && parsed.files ? parsed : emptyManifest();
  } catch { return emptyManifest(); }
}

async function writeManifest(root: FileSystemDirectoryHandle, manifest: SyncManifest) {
  manifest.updatedAt = new Date().toISOString();
  const handle = await root.getFileHandle(MANIFEST_NAME, { create: true });
  const writable = await handle.createWritable();
  await writable.write(JSON.stringify(manifest, null, 2));
  await writable.close();
}

async function ensureDirectory(root: FileSystemDirectoryHandle, names: string[]) {
  let current = root;
  for (const name of names) current = await current.getDirectoryHandle(safeWindowsName(name), { create: true });
  return current;
}

async function existingMatches(root: FileSystemDirectoryHandle, entry: SyncManifestEntry) {
  try {
    const parts = entry.relativePath.split("/");
    const filename = parts.pop()!;
    let dir = root;
    for (const part of parts) dir = await dir.getDirectoryHandle(part);
    const local = await (await dir.getFileHandle(filename)).getFile();
    if (local.size !== entry.size) return false;
    return (await sha256(local)) === entry.sha256;
  } catch { return false; }
}

async function fileAt(root: FileSystemDirectoryHandle, relativePath: string) {
  const parts = relativePath.split("/"); const filename = parts.pop()!; let dir = root;
  for (const part of parts) dir = await dir.getDirectoryHandle(part);
  return { dir, filename, file: await (await dir.getFileHandle(filename)).getFile() };
}

async function moveLocalFile(root: FileSystemDirectoryHandle, fromPath: string, toPath: string) {
  const source = await fileAt(root, fromPath); const parts = toPath.split("/"); const filename = parts.pop()!;
  const targetDir = await ensureDirectory(root, parts); const target = await targetDir.getFileHandle(filename, { create: true });
  const writable = await target.createWritable(); await source.file.stream().pipeTo(writable); await source.dir.removeEntry(source.filename);
}

function chooseName(file: AuditionFile, copyNumber = 1) {
  if (copyNumber <= 1) return safeWindowsName(file.originalName);
  const dot = file.originalName.lastIndexOf(".");
  const base = dot > 0 ? file.originalName.slice(0, dot) : file.originalName;
  const ext = dot > 0 ? file.originalName.slice(dot) : "";
  return safeWindowsName(`${base}_${copyNumber}${ext}`);
}

function chooseZipName(file: AuditionFile, folder: string, used: Set<string>) {
  let copyNumber = 1; let filename = chooseName(file, copyNumber);
  while (used.has(`${folder}/${filename}`.toLowerCase())) filename = chooseName(file, ++copyNumber);
  return filename;
}

async function chooseLocalName(dir: FileSystemDirectoryHandle, file: AuditionFile) {
  for (let copyNumber = 2; copyNumber < 10000; copyNumber++) {
    const filename = chooseName(file, copyNumber);
    try { await dir.getFileHandle(filename); } catch { return filename; }
  }
  throw new Error(`${file.originalName} 的同名文件过多`);
}

export interface SyncProgress { done: number; total: number; downloaded: number; downloadedBytes: number; skipped: number; failed: number; current?: string; error?: string; }

const ZIP_SYNC_KEY = "cmvfilm-zip-sync-v1";

function zipManifest(projectId: string): Record<string, string> {
  try { return JSON.parse(localStorage.getItem(`${ZIP_SYNC_KEY}:${projectId}`) || "{}"); } catch { return {}; }
}

function crc32(bytes: Uint8Array) {
  let crc = 0xffffffff;
  for (const byte of bytes) { crc ^= byte; for (let bit = 0; bit < 8; bit++) crc = (crc >>> 1) ^ (crc & 1 ? 0xedb88320 : 0); }
  return (crc ^ 0xffffffff) >>> 0;
}

function zipDate(date = new Date()) {
  return { time: (date.getHours() << 11) | (date.getMinutes() << 5) | (date.getSeconds() >> 1), date: ((date.getFullYear() - 1980) << 9) | ((date.getMonth() + 1) << 5) | date.getDate() };
}

function write16(view: DataView, offset: number, value: number) { view.setUint16(offset, value, true); }
function write32(view: DataView, offset: number, value: number) { view.setUint32(offset, value, true); }

function makeZip(entries: Array<{ path: string; bytes: Uint8Array }>) {
  const encoder = new TextEncoder(); const parts: BlobPart[] = []; const central: Uint8Array[] = []; let offset = 0;
  const blobPart = (bytes: Uint8Array): ArrayBuffer => Uint8Array.from(bytes).buffer;
  const stamp = zipDate();
  for (const entry of entries) {
    const name = encoder.encode(entry.path.replaceAll("\\", "/")); const crc = crc32(entry.bytes);
    const local = new Uint8Array(30 + name.length); const lv = new DataView(local.buffer);
    write32(lv, 0, 0x04034b50); write16(lv, 4, 20); write16(lv, 6, 0x0800); write16(lv, 8, 0); write16(lv, 10, stamp.time); write16(lv, 12, stamp.date);
    write32(lv, 14, crc); write32(lv, 18, entry.bytes.length); write32(lv, 22, entry.bytes.length); write16(lv, 26, name.length); write16(lv, 28, 0); local.set(name, 30);
    const directory = new Uint8Array(46 + name.length); const dv = new DataView(directory.buffer);
    write32(dv, 0, 0x02014b50); write16(dv, 4, 20); write16(dv, 6, 20); write16(dv, 8, 0x0800); write16(dv, 10, 0); write16(dv, 12, stamp.time); write16(dv, 14, stamp.date);
    write32(dv, 16, crc); write32(dv, 20, entry.bytes.length); write32(dv, 24, entry.bytes.length); write16(dv, 28, name.length); write16(dv, 30, 0); write16(dv, 32, 0); write16(dv, 34, 0); write16(dv, 36, 0); write32(dv, 38, 0); write32(dv, 42, offset); directory.set(name, 46);
    parts.push(blobPart(local), blobPart(entry.bytes)); central.push(directory); offset += local.length + entry.bytes.length;
  }
  const centralSize = central.reduce((sum, item) => sum + item.length, 0); parts.push(...central.map(blobPart));
  const end = new Uint8Array(22); const ev = new DataView(end.buffer); write32(ev, 0, 0x06054b50); write16(ev, 4, 0); write16(ev, 6, 0); write16(ev, 8, entries.length); write16(ev, 10, entries.length); write32(ev, 12, centralSize); write32(ev, 16, offset); write16(ev, 20, 0);
  parts.push(blobPart(end)); return new Blob(parts, { type: "application/zip" });
}

export async function downloadProjectArchive(project: Project, files: AuditionFile[], resolveDownload: (file: AuditionFile) => Promise<Response>, onProgress: (value: SyncProgress) => void, mode: "new" | "all" = "new") {
  const manifest = zipManifest(project.id); const pending = mode === "all" ? files : files.filter((file) => manifest[file.id] !== file.sha256);
  const progress: SyncProgress = { done: files.length - pending.length, total: files.length, downloaded: 0, downloadedBytes: 0, skipped: files.length - pending.length, failed: 0 };
  onProgress({ ...progress }); if (!pending.length) return progress;
  const used = new Set<string>(); const entries: Array<{ path: string; bytes: Uint8Array; file: AuditionFile }> = [];
  for (const file of pending) {
    progress.current = file.originalName; progress.error = undefined; onProgress({ ...progress });
    try {
      const folder = `${safeWindowsName(project.name)}/${safeWindowsName(file.roleName)}`;
      const filename = chooseZipName(file, folder, used); const path = `${folder}/${filename}`;
      used.add(path.toLowerCase()); const response = await resolveDownload(file); if (!response.ok) throw new Error("下载失败");
      const bytes = new Uint8Array(await response.arrayBuffer()); entries.push({ path, bytes, file }); progress.downloaded++; progress.downloadedBytes += bytes.byteLength;
    } catch (error) { progress.failed++; progress.error = error instanceof Error ? error.message : "下载失败"; }
    progress.done++; onProgress({ ...progress });
  }
  if (entries.length) {
    const url = URL.createObjectURL(makeZip(entries)); const a = document.createElement("a"); a.href = url; a.download = `${safeWindowsName(project.name)}_${mode === "all" ? "全部试音" : "新增试音"}.zip`; a.click(); setTimeout(() => URL.revokeObjectURL(url), 30000);
    for (const entry of entries) manifest[entry.file.id] = entry.file.sha256;
    try { localStorage.setItem(`${ZIP_SYNC_KEY}:${project.id}`, JSON.stringify(manifest)); } catch { /* Download still succeeded. */ }
  }
  progress.current = undefined; onProgress({ ...progress }); return progress;
}

export async function syncProject(
  project: Project,
  files: AuditionFile[],
  resolveDownload: (file: AuditionFile) => Promise<Response>,
  onProgress: (value: SyncProgress) => void,
) {
  if (window.isSecureContext === false) throw new Error("一键同步需要 HTTPS 安全连接；当前网站仍是 HTTP，请先启用 HTTPS");
  if (!window.showDirectoryPicker) throw new Error("当前浏览器不支持文件夹同步，请使用最新版桌面 Chrome 或 Edge");
  const root = await window.showDirectoryPicker({ mode: "readwrite" });
  const manifest = await readManifest(root);
  const progress: SyncProgress = { done: 0, total: files.length, downloaded: 0, downloadedBytes: 0, skipped: 0, failed: 0 };
  for (const file of files) {
    progress.current = file.originalName; progress.error = undefined; onProgress({ ...progress });
    const recorded = manifest.files[file.id];
    if (recorded && await existingMatches(root, recorded)) {
      const desiredPath = `${safeWindowsName(project.name)}/${safeWindowsName(file.roleName)}/${recorded.relativePath.split("/").pop()}`;
      if (recorded.relativePath !== desiredPath) { await moveLocalFile(root, recorded.relativePath, desiredPath); recorded.relativePath = desiredPath; recorded.syncedAt = new Date().toISOString(); await writeManifest(root, manifest); }
      progress.skipped++; progress.done++; onProgress({ ...progress }); continue;
    }
    try {
      const dir = await ensureDirectory(root, [project.name, file.roleName]);
      let filename = chooseName(file);
      try {
        const existing = await (await dir.getFileHandle(filename)).getFile();
        if (existing.size === file.size && await sha256(existing) === file.sha256) {
          manifest.files[file.id] = { fileId: file.id, sha256: file.sha256, size: file.size, relativePath: `${safeWindowsName(project.name)}/${safeWindowsName(file.roleName)}/${filename}`, syncedAt: new Date().toISOString() };
          await writeManifest(root, manifest); progress.skipped++; progress.done++; onProgress({ ...progress }); continue;
        }
        filename = await chooseLocalName(dir, file);
      } catch { /* filename is free */ }
      const response = await resolveDownload(file);
      if (!response.ok || !response.body) throw new Error("下载失败");
      const target = await dir.getFileHandle(filename, { create: true });
      const writable = await target.createWritable();
      await response.body.pipeTo(writable);
      manifest.files[file.id] = { fileId: file.id, sha256: file.sha256, size: file.size, relativePath: `${safeWindowsName(project.name)}/${safeWindowsName(file.roleName)}/${filename}`, syncedAt: new Date().toISOString() };
      await writeManifest(root, manifest);
      progress.downloaded++; progress.downloadedBytes += file.size;
    } catch (error) { progress.failed++; progress.error = error instanceof Error ? error.message : "同步失败"; }
    progress.done++; onProgress({ ...progress });
  }
  progress.current = undefined; onProgress({ ...progress });
  return progress;
}
